import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';

export default function KontaktPage() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'error'>('idle');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const navigate = useNavigate();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useState(() => {
    const handleScroll = () => setShowBackToTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => formBody.append(k, v));
      const response = await fetch('https://readdy.ai/api/form/d6nog12hfaca3v9jd9g0', { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: formBody.toString() });
      if (response.ok) {
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
        navigate('/danke');
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'IGM Facility Service',
    image: `${siteUrl}/og-image.jpg`,
    description: 'Kontaktieren Sie IGM Facility Service für professionelle Gebäudereinigung, Hausmeisterservice und Schlüsseldienst in Ratingen.',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Industriestr. 37',
      addressLocality: 'Ratingen',
      postalCode: '40878',
      addressCountry: 'DE',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 51.2966,
      longitude: 6.8499,
    },
    url: siteUrl,
    telephone: '+492102739807',
    email: 'info@igmfacilityservice.de',
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '10:00',
        closes: '15:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Saturday',
        opens: '12:00',
        closes: '15:00',
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <SEO
        title="Kontakt | IGM Facility Service Ratingen | Jetzt Angebot anfragen"
        description="Kontaktieren Sie IGM Facility Service Ratingen: Tel. 02102 7398070, E-Mail info@igmfacilityservice.de. Industriestr. 37, 40878 Ratingen. Jetzt unverbindliches Angebot anfragen!"
        keywords="Kontakt IGM Facility Service, Angebot anfragen, Ratingen, Gebäudereinigung Kontakt, Hausmeisterservice Kontakt"
        canonical="/kontakt"
        jsonLd={jsonLd}
      />
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-[#0D2B4E]">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">Kontaktieren Sie uns</h1>
          <p className="text-base sm:text-lg md:text-xl text-white/70 max-w-3xl mx-auto">
            Wir sind für Sie da! Kontaktieren Sie uns für ein unverbindliches Angebot oder bei Fragen zu unseren Dienstleistungen.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: 'ri-map-pin-line', title: 'Adresse', content: <p className="text-sm text-gray-600 leading-relaxed">Industriestr. 37<br />40878 Ratingen<br />Deutschland</p> },
              { icon: 'ri-phone-line', title: 'Telefon', content: <><a href="tel:+492102739807" className="text-sm text-[#0D2B4E] hover:underline font-medium block mb-1 min-h-[44px] flex items-center">02102 7398070</a><p className="text-xs text-gray-500">Notdienst: 24/7 erreichbar</p></> },
              { icon: 'ri-mail-line', title: 'E-Mail', content: <><a href="mailto:info@igmfacilityservice.de" className="text-sm text-[#0D2B4E] hover:underline font-medium block mb-1 min-h-[44px] flex items-center break-all">info@igmfacilityservice.de</a><p className="text-xs text-gray-500">Antwort innerhalb 24h</p></> },
              { icon: 'ri-time-line', title: 'Öffnungszeiten', content: <p className="text-sm text-gray-600 leading-relaxed">Mo-Fr: 10:00 - 15:00<br />Sa: 12:00 - 15:00<br />Notdienst: 24/7</p> },
            ].map((card, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-14 h-14 bg-[#0D2B4E] rounded-lg flex items-center justify-center mb-4">
                  <i className={`${card.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">{card.title}</h3>
                {card.content}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Senden Sie uns eine Nachricht</h2>
            <p className="text-base sm:text-lg text-gray-600">Füllen Sie das Formular aus und wir melden uns schnellstmöglich bei Ihnen.</p>
          </div>
          <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="bg-gray-50 rounded-2xl p-6 sm:p-8 md:p-12 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">Name *</label>
                <input type="text" id="name" name="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D2B4E] focus:border-transparent outline-none transition-all min-h-[44px]" placeholder="Ihr vollständiger Name" />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">E-Mail *</label>
                <input type="email" id="email" name="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D2B4E] focus:border-transparent outline-none transition-all min-h-[44px]" placeholder="ihre.email@beispiel.de" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">Telefon *</label>
                <input type="tel" id="phone" name="phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D2B4E] focus:border-transparent outline-none transition-all min-h-[44px]" placeholder="+49 170 123 4567" />
              </div>
              <div>
                <label htmlFor="service" className="block text-sm font-semibold text-gray-700 mb-2">Dienstleistung *</label>
                <select id="service" name="service" value={formData.service} onChange={(e) => setFormData({ ...formData, service: e.target.value })} required className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D2B4E] focus:border-transparent outline-none transition-all cursor-pointer min-h-[44px]">
                  <option value="">Bitte wählen...</option>
                  <option value="Gebäudereinigung">Gebäudereinigung</option>
                  <option value="Hausmeisterservice">Hausmeisterservice</option>
                  <option value="Schlüsseldienst">Schlüsseldienst</option>
                  <option value="Fensterreinigung">Fensterreinigung</option>
                  <option value="Winterdienst">Winterdienst</option>
                  <option value="Umzugsreinigung">Umzugsreinigung</option>
                  <option value="Sonstiges">Sonstiges</option>
                </select>
              </div>
            </div>
            <div className="mb-6">
              <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">Nachricht *</label>
              <textarea id="message" name="message" value={formData.message} onChange={(e) => { if (e.target.value.length <= 500) setFormData({ ...formData, message: e.target.value }); }} required rows={6} maxLength={500} className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D2B4E] focus:border-transparent outline-none transition-all resize-none" placeholder="Beschreiben Sie Ihr Anliegen..."></textarea>
              <p className="text-xs text-gray-500 mt-2">{formData.message.length}/500 Zeichen</p>
            </div>
            <button type="submit" disabled={isSubmitting} className="w-full bg-[#0D2B4E] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#0a2240] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer min-h-[44px]">
              {isSubmitting ? (
                <span className="flex items-center justify-center space-x-2"><i className="ri-loader-4-line animate-spin"></i><span>Wird gesendet...</span></span>
              ) : (
                <span className="flex items-center justify-center space-x-2"><i className="ri-send-plane-line"></i><span>Nachricht senden</span></span>
              )}
            </button>
            {submitStatus === 'error' && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
                <i className="ri-error-warning-line text-xl text-red-600 mt-0.5"></i>
                <div>
                  <p className="text-sm font-semibold text-red-800">Fehler beim Senden</p>
                  <p className="text-sm text-red-700 mt-1">Bitte versuchen Sie es erneut oder kontaktieren Sie uns telefonisch.</p>
                </div>
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">So finden Sie uns</h2>
            <p className="text-base sm:text-lg text-gray-600">Besuchen Sie uns in unserem Büro in Ratingen</p>
          </div>
          <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
            <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2494.0!2d6.8499!3d51.2966!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47b8b0e1e1e1e1e1%3A0x1!2sIndustriestr.+37%2C+40878+Ratingen!5e0!3m2!1sde!2sde!4v1234567890" width="100%" height="300" className="sm:h-[400px] md:h-[500px]" style={{ border: 0 }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="IGM Facility Service Location"></iframe>
          </div>
        </div>
      </section>

      <Footer />

      <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className={`fixed bottom-4 right-4 w-10 h-10 sm:w-12 sm:h-12 bg-[#0D2B4E] text-white rounded-full shadow-lg hover:bg-[#0a2240] transition-all duration-300 flex items-center justify-center cursor-pointer z-40 ${showBackToTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
        <i className="ri-arrow-up-line text-lg sm:text-xl"></i>
      </button>
    </div>
  );
}